<template>
    <main class="nv-page" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/news" class="tbv2-crumb">{{ $t('news.page.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ pageTitle }}</span>
            </div>
        </portal>

        <div v-if="isLoading" class="nv-loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <template v-else-if="article">
            <!-- ── Top bar ────────────────────────────────────── -->
            <div class="nv-topbar">
                <router-link to="/news" class="nv-back">
                    <i class="fal fa-arrow-left"></i>
                    <span>{{ $t('common.back') }}</span>
                </router-link>

                <div v-if="canUpdate" class="nv-topbar-actions">
                    <BmvButton
                        v-if="article.status === 'draft'"
                        variant="success"
                        size="sm"
                        icon="fal fa-paper-plane"
                        :loading="busyAction === 'publish'"
                        @click="doPublish"
                    >
                        {{ $t('news.actions.publish') }}
                    </BmvButton>
                    <BmvButton
                        v-else
                        variant="secondary"
                        size="sm"
                        icon="fal fa-ban"
                        :loading="busyAction === 'unpublish'"
                        @click="doUnpublish"
                    >
                        {{ $t('news.actions.unpublish') }}
                    </BmvButton>
                    <BmvButton
                        variant="primary"
                        size="sm"
                        icon="fal fa-pen"
                        @click="goToEdit"
                    >
                        {{ $t('common.edit') }}
                    </BmvButton>
                </div>
            </div>

            <!-- ── Layout 2 colonnes ──────────────────────────── -->
            <div class="nv-content-wrap">
                <div class="nv-layout">

                    <!-- Colonne gauche : contenu -->
                    <div class="nv-card nv-card--main">
                        <div class="nv-card-body">

                            <div class="nv-meta">
                                <span
                                    class="nv-badge"
                                    :class="article.status === 'published' ? 'nv-badge--pub' : 'nv-badge--draft'"
                                >
                                    <i :class="article.status === 'published' ? 'fal fa-check-circle' : 'fal fa-pen'"></i>
                                    {{ $t(`news.status.${article.status || 'draft'}`) }}
                                </span>
                                <span v-if="category" class="nv-category">
                                    {{ category }}
                                </span>
                            </div>

                            <h1 class="nv-title">{{ pageTitle }}</h1>

                            <div class="nv-byline">
                                <span v-if="authorName" class="nv-byline-item">
                                    <i class="fal fa-user-circle"></i>
                                    {{ authorName }}
                                </span>
                                <span v-if="article.published_at" class="nv-byline-item">
                                    <i class="fal fa-calendar-alt"></i>
                                    {{ formatDate(article.published_at) }}
                                </span>
                            </div>

                            <div v-if="hasEnContent" class="nv-tabs">
                                <button
                                    type="button"
                                    class="nv-tab"
                                    :class="{ 'is-active': activeLang === 'fr' }"
                                    @click="activeLang = 'fr'"
                                >
                                    {{ $t('news.form.tab_fr') }}
                                </button>
                                <button
                                    type="button"
                                    class="nv-tab"
                                    :class="{ 'is-active': activeLang === 'en' }"
                                    @click="activeLang = 'en'"
                                >
                                    {{ $t('news.form.tab_en') }}
                                </button>
                            </div>

                            <div class="nv-divider" />

                            <article class="nv-body">
                                <div
                                    v-if="currentContent"
                                    class="nv-article-content ck-content"
                                    v-html="currentContent"
                                />
                                <p v-else class="nv-empty">
                                    {{ $t('news.page.view_no_content') }}
                                </p>
                            </article>

                        </div>
                    </div>

                    <!-- Colonne droite : image de couverture -->
                    <aside v-if="coverUrl" class="nv-card nv-card--cover">
                        <img :src="coverUrl" :alt="pageTitle" />
                    </aside>

                </div><!-- /.nv-layout -->
            </div><!-- /.nv-content-wrap -->
        </template>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'

export default {
    name: 'NewsView',
    components: { BmvButton },
    data() {
        return {
            isLoading: true,
            busyAction: null,
            activeLang: 'fr'
        }
    },
    computed: {
        ...mapState('news', { article: (state) => state.article }),
        lang() {
            return this.$store.state.lang
        },
        articleId() {
            return this.$route.params.id
        },
        translation() {
            if (!this.article) return null
            const translations = this.article.translations || []
            return (
                translations.find((t) => t.locale === this.activeLang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0] ||
                null
            )
        },
        pageTitle() {
            if (!this.article) return ''
            const translations = this.article.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && match.title) || `#${this.article.id}`
        },
        currentContent() {
            return (this.translation && this.translation.content) || ''
        },
        hasEnContent() {
            if (!this.article) return false
            const translations = this.article.translations || []
            const en = translations.find((t) => t.locale === 'en')
            return !!(en && (en.title || en.content))
        },
        coverUrl() {
            const media = this.article && this.article.cover_media
            if (!media) return null
            return media.base64 || media.url || media.path || null
        },
        category() {
            const cat = this.article && this.article.category
            if (!cat) return null
            const translations = cat.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && (match.name || match.title)) || cat.name || null
        },
        authorName() {
            const author = this.article && this.article.created_by
            if (!author) return null
            return author.name || author.email || null
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canUpdate() {
            return this.permissions.includes('news.update')
        }
    },
    created() {
        this.loadArticle()
    },
    methods: {
        ...mapActions('news', ['getArticle', 'publishArticle', 'unpublishArticle']),
        async loadArticle() {
            this.isLoading = true
            try {
                await this.getArticle(this.articleId)
                if (this.article) {
                    const translations = this.article.translations || []
                    const hasLang = translations.some((t) => t.locale === this.lang)
                    this.activeLang = hasLang ? this.lang : 'fr'
                }
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
                this.$router.push({ name: 'News' })
            } finally {
                this.isLoading = false
            }
        },
        formatDate(iso) {
            return this.moment(iso).locale(this.lang).format('Do MMMM YYYY')
        },
        goToEdit() {
            this.$router.push({ name: 'NewsEdit', params: { id: this.articleId } })
        },
        async doPublish() {
            if (this.busyAction) return
            this.busyAction = 'publish'
            try {
                await this.publishArticle(this.articleId)
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('news.actions.publish')
                })
                await this.loadArticle()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyAction = null
            }
        },
        async doUnpublish() {
            if (this.busyAction) return
            this.busyAction = 'unpublish'
            try {
                await this.unpublishArticle(this.articleId)
                this.$store.dispatch('toast', {
                    type: 'success',
                    title: this.$t('news.actions.unpublish')
                })
                await this.loadArticle()
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyAction = null
            }
        }
    }
}
</script>

<style scoped>
.nv-page {
    width: 100%;
    padding-bottom: 80px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.nv-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 100px 0;
}

/* ── Top bar ────────────────────────────────────────────────── */
.nv-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 32px 16px;
}

.nv-back {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.875rem;
    color: var(--v2-text-3);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.nv-back:hover {
    color: var(--v2-text-1);
}

.nv-topbar-actions {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
}

/* ── Main content ───────────────────────────────────────────── */
.nv-content-wrap {
    padding: 0 32px 32px;
}

/* ── 2-column layout ────────────────────────────────────────── */
.nv-layout {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 20px;
    align-items: start;
}

/* ── Cards (shared) ─────────────────────────────────────────── */
.nv-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-sm);
    overflow: hidden;
}

/* ── Left card: content ─────────────────────────────────────── */
.nv-card-body {
    padding: 24px 28px 28px;
    min-width: 0;
}

/* ── Right card: cover image ────────────────────────────────── */
.nv-card--cover {
    position: sticky;
    top: 16px;
}

.nv-card--cover img {
    width: 100%;
    height: auto;
    display: block;
    aspect-ratio: 4 / 3;
    object-fit: cover;
}

/* ── Meta ───────────────────────────────────────────────────── */
.nv-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.nv-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: var(--v2-radius-full);
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.nv-badge i {
    font-size: 0.68rem;
}

.nv-badge--draft {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.nv-badge--pub {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.nv-category {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: var(--v2-radius-full);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

/* ── Title ──────────────────────────────────────────────────── */
.nv-title {
    margin: 0 0 10px;
    font-size: 2rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.025em;
    line-height: 1.2;
    word-break: break-word;
}

/* ── Byline ─────────────────────────────────────────────────── */
.nv-byline {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.nv-byline-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8125rem;
    color: var(--v2-text-3);
}

.nv-byline-item i {
    font-size: 0.8rem;
}

/* ── Language tabs ──────────────────────────────────────────── */
.nv-tabs {
    display: flex;
    gap: 0;
    margin-bottom: 0;
}

.nv-tab {
    padding: 10px 18px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.nv-tab:hover {
    color: var(--v2-text-1);
}

.nv-tab.is-active {
    color: var(--v2-primary);
    border-bottom-color: var(--v2-primary);
    font-weight: 600;
}

/* ── Divider ────────────────────────────────────────────────── */
.nv-divider {
    height: 1px;
    background: var(--v2-border);
    margin: 16px 0 28px;
}

/* ── Article body ───────────────────────────────────────────── */
.nv-empty {
    color: var(--v2-text-4);
    font-style: italic;
    font-size: 0.9rem;
}

.nv-article-content.ck-content ::v-deep p {
    margin: 0 0 1.1em;
    font-size: 0.9375rem;
    line-height: 1.75;
    color: var(--v2-text-2);
}

.nv-article-content.ck-content ::v-deep p:last-child {
    margin-bottom: 0;
}

.nv-article-content.ck-content ::v-deep h2,
.nv-article-content.ck-content ::v-deep h3,
.nv-article-content.ck-content ::v-deep h4 {
    font-weight: 700;
    color: var(--v2-text-1);
    line-height: 1.25;
}

.nv-article-content.ck-content ::v-deep h2 {
    font-size: 1.5rem;
    margin: 1.8em 0 0.5em;
    letter-spacing: -0.01em;
}
.nv-article-content.ck-content ::v-deep h3 {
    font-size: 1.2rem;
    margin: 1.5em 0 0.4em;
}
.nv-article-content.ck-content ::v-deep h4 {
    font-size: 1rem;
    margin: 1.3em 0 0.4em;
}

.nv-article-content.ck-content ::v-deep ul,
.nv-article-content.ck-content ::v-deep ol {
    padding-left: 1.6em;
    margin: 0 0 1.1em;
}
.nv-article-content.ck-content ::v-deep ul { list-style: disc; }
.nv-article-content.ck-content ::v-deep ol { list-style: decimal; }
.nv-article-content.ck-content ::v-deep li {
    margin-bottom: 4px;
    line-height: 1.7;
    color: var(--v2-text-2);
}

.nv-article-content.ck-content ::v-deep blockquote {
    border-left: 3px solid var(--v2-primary);
    margin: 1.2em 0;
    padding: 10px 18px;
    color: var(--v2-text-2);
    font-style: italic;
    background: var(--v2-primary-soft);
    border-radius: 0 var(--v2-radius-sm) var(--v2-radius-sm) 0;
}

.nv-article-content.ck-content ::v-deep a {
    color: var(--v2-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
}
.nv-article-content.ck-content ::v-deep a:hover {
    color: var(--v2-primary-hover);
}

.nv-article-content.ck-content ::v-deep img {
    max-width: 100%;
    border-radius: var(--v2-radius-md);
    margin: 0.5em 0;
}

.nv-article-content.ck-content ::v-deep figure.table {
    margin: 1em 0;
    display: block;
    overflow-x: auto;
}
.nv-article-content.ck-content ::v-deep table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.9rem;
}
.nv-article-content.ck-content ::v-deep table td,
.nv-article-content.ck-content ::v-deep table th {
    border: 1px solid var(--v2-border);
    padding: 9px 12px;
    vertical-align: top;
}
.nv-article-content.ck-content ::v-deep table th {
    background: var(--v2-surface-1);
    font-weight: 600;
    color: var(--v2-text-1);
}

/* ── Responsive ─────────────────────────────────────────────── */
@media (max-width: 900px) {
    .nv-topbar {
        padding: 16px 18px 12px;
    }
    .nv-content-wrap {
        padding: 0 18px 24px;
    }
    /* Image passe au-dessus du contenu en colonne unique */
    .nv-layout {
        grid-template-columns: 1fr;
    }
    .nv-card--cover {
        position: static;
        order: -1;
    }
    .nv-card--cover img {
        aspect-ratio: 16 / 7;
    }
    .nv-card-body {
        padding: 18px 18px 22px;
    }
    .nv-title {
        font-size: 1.6rem;
    }
}

@media (max-width: 600px) {
    .nv-title {
        font-size: 1.35rem;
    }
    .nv-card--cover img {
        aspect-ratio: 4 / 3;
    }
}
</style>
